import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiGet } from "../lib/api";
import { apiPost } from "./api-ecrire";
import {
  type Drapeaux,
  drapeauxSchema,
  type GesteDetail,
  type GesteFaitDemande,
  type GesteFaitReponse,
  gesteDetailSchema,
  gesteFaitReponseSchema,
  type IdeeDetail,
  ideeDetailSchema,
  type Portefeuille,
  portefeuilleSchema,
  type RelanceDetail,
  relanceDetailSchema,
  type ScoutDetail,
  scoutDetailSchema,
} from "./contract";
import { noterDemande } from "./portefeuille-demandes";

/**
 * Les sept lectures de la console, et la seule ecriture du portefeuille. Aucune lecture ne
 * reessaye : un 401 ou un 403 est une reponse, pas une panne, et rejouer la requete ne ferait
 * que retarder l'ecran qui l'explique. Un 404 non plus n'a pas de deuxieme chance.
 */
const commun = { retry: false, refetchOnWindowFocus: false } as const;

/**
 * Une relecture periodique, decidee sur ce que la lecture vient de rendre : un delai en
 * millisecondes, ou `false` pour ne pas relire. L'accueil et la page d'un guide en posent une
 * tant qu'une demande « C'est fait » attend son commit.
 */
export type Relecture<T> = (lu: T | undefined) => number | false;

export function usePortefeuille(
  relecture?: Relecture<Portefeuille>,
): UseQueryResult<Portefeuille, Error> {
  return useQuery({
    queryKey: ["portefeuille"],
    queryFn: () => apiGet("/api/portefeuille", portefeuilleSchema),
    refetchInterval: (query) => relecture?.(query.state.data) ?? false,
    ...commun,
  });
}

export function useIdee(slug: string): UseQueryResult<IdeeDetail, Error> {
  return useQuery({
    queryKey: ["portefeuille", "idee", slug],
    queryFn: () => apiGet(`/api/portefeuille/idees/${encodeURIComponent(slug)}`, ideeDetailSchema),
    ...commun,
  });
}

export function useScout(id: string): UseQueryResult<ScoutDetail, Error> {
  return useQuery({
    queryKey: ["portefeuille", "scout", id],
    queryFn: () => apiGet(`/api/portefeuille/scout/${encodeURIComponent(id)}`, scoutDetailSchema),
    ...commun,
  });
}

/**
 * Les trois lectures d'un guide. La cle de cache porte la cible du geste, celle qui
 * s'ecrit dans l'adresse : deux idees peuvent tres bien avoir un guide du meme nom.
 */
export function useGesteIdee(
  slug: string,
  nom: string,
  relecture?: Relecture<GesteDetail>,
): UseQueryResult<GesteDetail, Error> {
  return useQuery({
    queryKey: ["portefeuille", "geste", slug, nom],
    queryFn: () =>
      apiGet(
        `/api/portefeuille/idees/${encodeURIComponent(slug)}/gestes/${encodeURIComponent(nom)}`,
        gesteDetailSchema,
      ),
    refetchInterval: (query) => relecture?.(query.state.data) ?? false,
    ...commun,
  });
}

export function useGesteHorsIdee(
  nom: string,
  relecture?: Relecture<GesteDetail>,
): UseQueryResult<GesteDetail, Error> {
  return useQuery({
    queryKey: ["portefeuille", "geste", "hors-idee", nom],
    queryFn: () => apiGet(`/api/portefeuille/gestes/${encodeURIComponent(nom)}`, gesteDetailSchema),
    refetchInterval: (query) => relecture?.(query.state.data) ?? false,
    ...commun,
  });
}

export function useRelance(slug: string): UseQueryResult<RelanceDetail, Error> {
  return useQuery({
    queryKey: ["portefeuille", "relance", slug],
    queryFn: () =>
      apiGet(`/api/portefeuille/idees/${encodeURIComponent(slug)}/relance`, relanceDetailSchema),
    ...commun,
  });
}

/**
 * Les drapeaux du bandeau. Ils ne sont pas lisibles sans session : la requete est donc
 * inutile tant que la page principale repond 401, d'ou le drapeau `active`.
 */
export function useDrapeaux(active: boolean): UseQueryResult<Drapeaux, Error> {
  return useQuery({
    queryKey: ["console", "drapeaux"],
    queryFn: () => apiGet("/api/console/drapeaux", drapeauxSchema),
    enabled: active,
    ...commun,
  });
}

/**
 * `POST /api/portefeuille/gestes/fait`, le bouton « C'est fait ». Le 202 dit que le workflow
 * est parti, pas que le geste est coche : la demande est notee dans le navigateur, et les
 * ecrans se relisent jusqu'a voir le commit. Un 409 dit que la page est en retard sur le
 * depot : tout le portefeuille se relit. Pas de nouvel essai : un refus s'affiche, avec la
 * commande de secours.
 */
export function useGesteFait(): UseMutationResult<GesteFaitReponse, Error, GesteFaitDemande> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (demande: GesteFaitDemande) =>
      apiPost("/api/portefeuille/gestes/fait", demande, gesteFaitReponseSchema),
    onSuccess: (reponse, demande) => {
      noterDemande(demande.cible, reponse.demandeLe, Date.now());
    },
    onError: (error) => {
      if (statutDe(error) === 409) void client.invalidateQueries({ queryKey: ["portefeuille"] });
    },
  });
}

/** Le statut HTTP d'une erreur de lecture, ou `null` quand elle vient d'ailleurs. */
export function statutDe(error: unknown): number | null {
  return error instanceof ApiError ? error.status : null;
}
